import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { Loader2, Upload, X, ChevronLeft, Check, Car, Camera, Heart } from "lucide-react";
import { toast } from "sonner";

const initialState = {
  membership_type: "car_owner",
  name: "",
  mobile: "",
  email: "",
  plate_number: "",
  car_make: "",
  car_model: "",
  car_year: "",
  notes: "",
  photos: [],
  car_ready: true,
  expected_ready_date: "",
  instagram_handle: "",
  snapchat_handle: "",
};

const TIERS = [
  {
    key: "car_owner",
    label: "مالك سيارة",
    desc: "كلاسيك أو معدّلة — انضم بسيارتك",
    icon: Car,
  },
  {
    key: "photographer",
    label: "مصوّر",
    desc: "وثّق التجمعات وكن جزءاً من الذاكرة البصرية",
    icon: Camera,
  },
  {
    key: "fan",
    label: "هاوي / متابع",
    desc: "تابع أخبار النادي واحضر التجمعات بدون سيارة",
    icon: Heart,
  },
];

const fileToDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function Register() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowed = files.slice(0, 6 - form.photos.length);
    const dataURLs = await Promise.all(
      allowed.map(async (f) => {
        if (f.size > 4 * 1024 * 1024) {
          toast.error(`${f.name}: الحد الأقصى 4 ميجابايت لكل صورة`);
          return null;
        }
        const data = await fileToDataURL(f);
        return { data, name: f.name };
      })
    );
    setForm((f) => ({
      ...f,
      photos: [...f.photos, ...dataURLs.filter(Boolean)],
    }));
    e.target.value = "";
  };

  const removePhoto = (i) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const isCarOwner = form.membership_type === "car_owner";
  const isPhotographer = form.membership_type === "photographer";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || !form.email.trim()) {
      toast.error("الاسم والجوال والبريد الإلكتروني مطلوبة");
      return;
    }
    const mobileDigits = form.mobile.replace(/\D/g, "");
    if (mobileDigits.length < 9) {
      toast.error("رقم الجوال غير صحيح. مثال: 0501234567");
      return;
    }
    if (isCarOwner) {
      if (!form.plate_number.trim()) {
        toast.error("رقم اللوحة مطلوب");
        return;
      }
      if (form.photos.length === 0) {
        toast.error("يرجى رفع صورة واحدة على الأقل لسيارتك");
        return;
      }
    }
    if (isPhotographer) {
      if (!form.instagram_handle.trim() && !form.snapchat_handle.trim()) {
        toast.error("يرجى إدخال حساب الإنستقرام أو السناب على الأقل");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.car_ready) payload.expected_ready_date = null;
      // Strip car-specific fields for non-owners
      if (!isCarOwner) {
        payload.plate_number = null;
        payload.car_make = "";
        payload.car_model = "";
        payload.car_year = "";
        payload.photos = [];
        payload.car_ready = true;
        payload.expected_ready_date = null;
      }
      if (!isPhotographer) {
        payload.instagram_handle = "";
        payload.snapchat_handle = "";
      }
      const { data } = await api.post("/registrations", payload);
      toast.success("تم استلام طلب عضويتك بنجاح");
      navigate(`/success/${data.reference_code}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "حدث خطأ، حاول مرة أخرى";
      toast.error(typeof msg === "string" ? msg : "بيانات غير صحيحة");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-[2px] w-10 bg-rb-red" />
            <span className="text-xs tracking-[0.4em] text-rb-red font-bold">
              CLUB MEMBERSHIP
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.95] mb-4 text-rb-ink">
            انضم لنادي RB100
          </h1>
          <p className="text-rb-textMuted text-base leading-loose max-w-xl">
            اختر نوع عضويتك أدناه، أدخل بياناتك، وسيتم مراجعة طلبك من قبل لجنة
            النادي. ستصلك رسالة الاعتماد عبر واتساب.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-7" data-testid="registration-form">
          <FieldGroup title="نوع العضوية *">
            <div className="grid sm:grid-cols-3 gap-3">
              {TIERS.map((t) => {
                const Icon = t.icon;
                const selected = form.membership_type === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => onChange("membership_type", t.key)}
                    data-testid={`tier-${t.key}`}
                    className={`text-right p-5 border transition-all ${
                      selected
                        ? "border-rb-red bg-rb-red/5"
                        : "border-rb-border bg-white hover:border-rb-ink/40"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <Icon className={`w-7 h-7 ${selected ? "text-rb-red" : "text-rb-textMuted"}`} />
                      {selected && (
                        <span className="bg-rb-red text-white w-6 h-6 flex items-center justify-center shrink-0">
                          <Check className="w-4 h-4" />
                        </span>
                      )}
                    </div>
                    <div className="font-bold text-rb-ink text-base mb-1">{t.label}</div>
                    <div className="text-xs text-rb-textMuted leading-relaxed">{t.desc}</div>
                  </button>
                );
              })}
            </div>
          </FieldGroup>

          <FieldGroup title="بياناتك الأساسية">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="الاسم الكامل *" testid="field-name">
                <Input value={form.name} onChange={(v) => onChange("name", v)} placeholder="مثال: عبدالله الراشد" testid="input-name" />
              </Field>
              <Field label="رقم الجوال *" testid="field-mobile">
                <Input value={form.mobile} onChange={(v) => onChange("mobile", v)} placeholder="05XXXXXXXX أو 9665XXXXXXXX" testid="input-mobile" type="tel" />
              </Field>
              <Field label="البريد الإلكتروني *" testid="field-email">
                <Input value={form.email} onChange={(v) => onChange("email", v)} placeholder="name@example.com" testid="input-email" type="email" />
              </Field>
              {isCarOwner && (
                <Field label="لوحة السيارة *" testid="field-plate">
                  <Input value={form.plate_number} onChange={(v) => onChange("plate_number", v.toUpperCase())} placeholder="مثال: ABC 1234" testid="input-plate" />
                </Field>
              )}
            </div>
          </FieldGroup>

          {isCarOwner && (
            <>
              <FieldGroup title="معلومات السيارة">
                <div className="grid sm:grid-cols-3 gap-5">
                  <Field label="الماركة" testid="field-make">
                    <Input value={form.car_make} onChange={(v) => onChange("car_make", v)} placeholder="Porsche" testid="input-make" />
                  </Field>
                  <Field label="الموديل" testid="field-model">
                    <Input value={form.car_model} onChange={(v) => onChange("car_model", v)} placeholder="911 Carrera" testid="input-model" />
                  </Field>
                  <Field label="سنة الصنع" testid="field-year">
                    <Input value={form.car_year} onChange={(v) => onChange("car_year", v)} placeholder="1985" testid="input-year" />
                  </Field>
                </div>
              </FieldGroup>

              <FieldGroup title="صور السيارة *">
                <p className="text-sm text-rb-textMuted mb-4">
                  ارفع حتى 6 صور، الحد الأقصى 4 ميجابايت لكل صورة
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {form.photos.map((p, i) => (
                    <div
                      key={i}
                      className="relative aspect-square bg-rb-surface border border-rb-border overflow-hidden group"
                    >
                      <img src={p.data} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        data-testid={`photo-remove-${i}`}
                        className="absolute top-2 left-2 bg-rb-red text-white w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {form.photos.length < 6 && (
                    <label
                      data-testid="photo-upload-label"
                      className="aspect-square bg-rb-surface border-2 border-dashed border-rb-border hover:border-rb-red transition-colors flex flex-col items-center justify-center cursor-pointer text-rb-textMuted hover:text-rb-red"
                    >
                      <Upload className="w-7 h-7 mb-2" />
                      <span className="text-xs">إضافة صور</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={onPhotos}
                        data-testid="input-photos"
                      />
                    </label>
                  )}
                </div>
              </FieldGroup>

              <FieldGroup title="جاهزية السيارة للمشاركة">
                <p className="text-sm text-rb-textMuted mb-4">
                  هل سيارتك جاهزة الآن للمشاركة في فعاليات النادي القادمة؟
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <ChoiceCard
                    selected={form.car_ready === true}
                    onClick={() => onChange("car_ready", true)}
                    label="نعم، جاهزة الآن"
                    desc="السيارة جاهزة للمشاركة بأي وقت"
                    testid="choice-ready-yes"
                  />
                  <ChoiceCard
                    selected={form.car_ready === false}
                    onClick={() => onChange("car_ready", false)}
                    label="ليست جاهزة حالياً"
                    desc="قيد التجهيز/الصيانة"
                    testid="choice-ready-no"
                  />
                </div>

                {form.car_ready === false && (
                  <div
                    data-testid="ready-details"
                    className="mt-5 bg-rb-surface border border-rb-border p-5"
                  >
                    <Field label="الموعد التقريبي للجاهزية *" testid="field-expected-ready">
                      <Input
                        value={form.expected_ready_date}
                        onChange={(v) => onChange("expected_ready_date", v)}
                        placeholder="مثال: خلال شهرين / منتصف رمضان / يونيو 2026"
                        testid="input-expected-ready"
                      />
                    </Field>
                  </div>
                )}
              </FieldGroup>
            </>
          )}

          {isPhotographer && (
            <FieldGroup title="حسابات التصوير *">
              <p className="text-sm text-rb-textMuted mb-4">
                أدخل حساباتك على إنستقرام و/أو سناب شات حتى نتمكن من متابعة أعمالك
                ومشاركتها مع النادي.
              </p>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="حساب الإنستقرام" testid="field-instagram">
                  <Input
                    value={form.instagram_handle}
                    onChange={(v) => onChange("instagram_handle", v)}
                    placeholder="@username"
                    testid="input-instagram"
                  />
                </Field>
                <Field label="حساب السناب شات" testid="field-snapchat">
                  <Input
                    value={form.snapchat_handle}
                    onChange={(v) => onChange("snapchat_handle", v)}
                    placeholder="@username"
                    testid="input-snapchat"
                  />
                </Field>
              </div>
            </FieldGroup>
          )}

          <FieldGroup title="ملاحظات إضافية (اختياري)">
            <textarea
              value={form.notes}
              onChange={(e) => onChange("notes", e.target.value)}
              rows={4}
              data-testid="input-notes"
              placeholder="أي معلومات إضافية تودّ إخبار اللجنة بها..."
              className="w-full bg-white border border-rb-border focus:border-rb-red focus:outline-none focus:ring-2 focus:ring-rb-red/20 px-4 py-3 text-rb-ink text-sm resize-none transition-colors placeholder:text-rb-textMuted/70"
            />
          </FieldGroup>

          <div className="flex flex-wrap gap-4 pt-4">
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-registration-btn"
              className="bg-rb-red hover:bg-rb-redHover disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold px-10 py-4 transition-colors flex items-center gap-3"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري الإرسال...
                </>
              ) : (
                <>
                  إرسال طلب العضوية
                  <ChevronLeft className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}

const FieldGroup = ({ title, children }) => (
  <div className="bg-white border border-rb-border p-6 md:p-8 shadow-sm">
    <h3 className="font-heading text-lg font-bold mb-5 flex items-center gap-3 text-rb-ink">
      <span className="h-[2px] w-6 bg-rb-red" />
      {title}
    </h3>
    {children}
  </div>
);

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-xs tracking-widest text-rb-textMuted uppercase mb-2">
      {label}
    </label>
    {children}
  </div>
);

const Input = ({ value, onChange, placeholder, type = "text", testid }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    className="w-full bg-white border border-rb-border focus:border-rb-red focus:outline-none focus:ring-2 focus:ring-rb-red/20 px-4 py-3 text-rb-ink text-sm transition-colors placeholder:text-rb-textMuted/70"
  />
);

const ChoiceCard = ({ selected, onClick, label, desc, testid, compact = false }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-right border transition-all ${compact ? "p-3" : "p-5"} ${
      selected
        ? "border-rb-red bg-rb-red/5"
        : "border-rb-border bg-white hover:border-rb-ink/40"
    }`}
  >
    <div className="flex items-start justify-between gap-2 mb-1">
      <span className={`font-bold text-rb-ink ${compact ? "text-sm" : "text-base"}`}>{label}</span>
      {selected && (
        <span className={`bg-rb-red text-white flex items-center justify-center shrink-0 ${compact ? "w-5 h-5" : "w-6 h-6"}`}>
          <Check className={compact ? "w-3 h-3" : "w-4 h-4"} />
        </span>
      )}
    </div>
    <span className={`text-rb-textMuted block ${compact ? "text-[11px]" : "text-xs"}`}>{desc}</span>
  </button>
);
